import {
  DynamicFee,
  FeeAppliesTo,
  FeeChargeBasis,
  FeeEligibilityScope,
  FeeType,
  IncentiveRule,
  RefundOptimizationRule,
  ShippingCostConfig,
  ShippingCostResponsibility,
} from 'types/incentive';
import { getStoreCurrencyFormatter } from 'utils';
import { summarizeFeeConditions } from './IncentiveSettings.feeRules';
import {
  hasIncentiveItemSelectors,
  normalizeIncentiveItemEligibility,
} from './IncentiveSettings.itemEligibility';

export const describeShippingCostConfig = (
  currency: string,
  config?: ShippingCostConfig,
) => {
  switch (config?.responsibility) {
    case ShippingCostResponsibility.CUSTOMER_PAYS_FULL:
      return 'Deduct carrier label cost';
    case ShippingCostResponsibility.CUSTOMER_PAYS_FLAT_FEE: {
      const currencyFees = Object.entries(
        config.flatFeeAmountsByCurrency || {},
      );
      if (currencyFees.length) {
        return `Deduct flat label fees: ${currencyFees
          .map(([code, amount]) => `${code.toUpperCase()} ${amount}`)
          .join(' · ')}. Default: ${
          config.flatFeeAmount ?? 0
        } in the order currency`;
      }
      return `Deduct ${getStoreCurrencyFormatter(
        currency,
        config.flatFeeAmount ?? 0,
      )} flat label fee`;
    }
    default:
      return 'Free return - merchant pays';
  }
};

type DynamicFeeSummaryInput = DynamicFee;

interface IncentiveConfigurationSummaryInput {
  currency: string;
  deflectionEnabled: boolean;
  rules: IncentiveRule[];
  refundOptimizationEnabled: boolean;
  optimizationRules: RefundOptimizationRule[];
  allowAdditionalFees: boolean;
  dynamicFees: DynamicFeeSummaryInput[];
  waiveFeesForProtectedOrders: boolean;
  smartEligibility: boolean;
}

export interface IncentiveConfigurationHighlight {
  label: string;
  value: string;
}

const formatCount = (count: number, singular: string) =>
  `${count} ${singular}${count === 1 ? '' : 's'}`;

const formatPercentage = (value: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);

const trimmedName = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const formatFee = (currency: string, fee: DynamicFeeSummaryInput) => {
  const name = trimmedName(fee.name) || 'Unnamed fee';
  const amount =
    fee.feeType === FeeType.PERCENTAGE
      ? `${formatPercentage(fee.amount)}%`
      : getStoreCurrencyFormatter(currency, fee.amount);

  const ruleSummary =
    fee.eligibility?.scope === FeeEligibilityScope.MATCHING_ITEMS
      ? ` · ${summarizeFeeConditions(fee)} · ${
          fee.chargeBasis === FeeChargeBasis.PER_MATCHING_UNIT
            ? 'per matching unit'
            : 'once per return'
        }`
      : '';

  return `${name}: ${amount}${ruleSummary}`;
};

export const getIncentiveConfigurationHighlights = ({
  currency,
  deflectionEnabled,
  rules,
  refundOptimizationEnabled,
  optimizationRules,
  allowAdditionalFees,
  dynamicFees,
  waiveFeesForProtectedOrders,
  smartEligibility,
}: IncentiveConfigurationSummaryInput): IncentiveConfigurationHighlight[] => {
  const activeKeepItemRules = deflectionEnabled
    ? rules.filter(
        (rule) => rule.isEnabled !== false && rule.options.length > 0,
      )
    : [];
  const activeKeepItemOfferCount = activeKeepItemRules.reduce(
    (count, rule) => count + rule.options.length,
    0,
  );
  const activeBonusRules = refundOptimizationEnabled
    ? optimizationRules.filter((rule) => rule.isEnabled !== false)
    : [];
  const restrictedKeepItemRuleCount = activeKeepItemRules.filter((rule) => {
    const eligibility = normalizeIncentiveItemEligibility(rule.eligibility);
    return (
      eligibility.scope !== 'ALL_ITEMS' ||
      hasIncentiveItemSelectors(eligibility.exclude)
    );
  }).length;
  const highestStoreCreditBonus = activeBonusRules.reduce(
    (highest, rule) => Math.max(highest, rule.bonusValue || 0),
    0,
  );
  const keepItemSummaryBase = !deflectionEnabled
    ? 'Off'
    : activeKeepItemOfferCount > 0
    ? `${formatCount(activeKeepItemOfferCount, 'offer')} · ${formatCount(
        activeKeepItemRules.length,
        'active rule',
      )}`
    : 'On · no active offers';
  const keepItemSummary = [
    keepItemSummaryBase,
    restrictedKeepItemRuleCount
      ? `${restrictedKeepItemRuleCount} rule${
          restrictedKeepItemRuleCount === 1 ? '' : 's'
        } with item restrictions`
      : '',
    deflectionEnabled && smartEligibility ? 'High-risk orders excluded' : '',
  ]
    .filter(Boolean)
    .join('\n');
  const storeCreditSummary = !refundOptimizationEnabled
    ? 'Off'
    : activeBonusRules.length > 0
    ? `Up to +${formatPercentage(highestStoreCreditBonus)}% · ${formatCount(
        activeBonusRules.length,
        'active rule',
      )}`
    : 'On · standard return value';
  const activeFees = allowAdditionalFees
    ? dynamicFees.filter(
        (fee) =>
          fee.enabled !== false &&
          Number.isFinite(fee.amount) &&
          fee.amount > 0,
      )
    : [];
  const refundFees = activeFees.filter(
    (fee) =>
      !fee.appliesTo ||
      fee.appliesTo === FeeAppliesTo.REFUND ||
      fee.appliesTo === FeeAppliesTo.BOTH,
  );
  const storeCreditFees = activeFees.filter(
    (fee) =>
      fee.appliesTo === FeeAppliesTo.STORE_CREDIT ||
      fee.appliesTo === FeeAppliesTo.BOTH,
  );
  const feeScopes = [
    refundFees.length > 0
      ? `Refund deductions:\n${refundFees
          .map((fee) => `• ${formatFee(currency, fee)}`)
          .join('\n')}`
      : '',
    storeCreditFees.length > 0
      ? `Store credit deductions:\n${storeCreditFees
          .map((fee) => `• ${formatFee(currency, fee)}`)
          .join('\n')}`
      : '',
    activeFees.length > 0 && waiveFeesForProtectedOrders
      ? 'Fees are waived for guaranteed orders.'
      : '',
  ].filter(Boolean);
  const additionalFeesSummary = !allowAdditionalFees
    ? 'Off'
    : activeFees.length > 0
    ? feeScopes.join('\n')
    : 'On · no customer deductions configured';
  return [
    { label: 'Keep-item offers', value: keepItemSummary },
    { label: 'Store credit', value: storeCreditSummary },
    { label: 'Additional fees', value: additionalFeesSummary },
  ];
};
